import { differenceInMilliseconds } from 'date-fns';
import {
  DRY_DOCK_OR_REPAIR_OPERATION_TYPE,
  FormState,
  OPERATING_CODE_OPTIONS,
  OperationalVm,
  PortCallPurpose,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { VesselParticulars, VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { getDate, getFieldFromFormState, getFieldValueFromFormState, isSectionValid } from '../vm-builder-helpers';
import { groupPortCallPurposes } from './port-call-reason.helpers';

const FALLBACK_CHARTER_PARTY_SPEED = 360;
const CONSTANT_WEIGHT_MIN = 0;
const CONSTANT_WEIGHT_MIN_ERROR = `Constant weight must be ≥ ${CONSTANT_WEIGHT_MIN} MT`;
const STS_OPERATING_CODE = '8';
const DEFAULT_MAX_REPORT_DURATION_HOURS = 26;

const getMaxReportDurationHours = (vesselSpec: VesselSpec | undefined): number =>
  vesselSpec?.information.report_duration_override ?? DEFAULT_MAX_REPORT_DURATION_HOURS;

const getOperatingCodeOptions = (vesselSpec: VesselSpec | undefined): SelectOption[] => {
  if (vesselSpec?.information.sts_voyage_allowed) return OPERATING_CODE_OPTIONS;

  return OPERATING_CODE_OPTIONS.filter((option) => option.code !== STS_OPERATING_CODE);
};

const ENTER_ECA_ERROR = 'Enter ECA timestamp must be between Report From and Report To dates.';
const ENTER_ECA_PREVIOUSLY_IN_ECA_ERROR =
  'Enter ECA timestamp must be between Report From and Report To dates, and after Exit ECA if vessel previously in ECA.';
const EXIT_ECA_ERROR =
  'Exit ECA timestamp must be between Report From and Report To dates, and after Enter ECA if vessel was not previously in ECA.';

const getReportDate = (formState: FormState, field: 'reportFrom' | 'reportTo' | 'eosp') =>
  getDate(getFieldValueFromFormState(formState, 'reportTime', field));

/**
 * The end of the report period. The form shows the EOSP field in place of
 * Report To while the ship arrives at EOSP, and the two are the same instant,
 * so every calculation that needs the end of the period reads this instead of
 * either field.
 *
 * The flag decides which field to read, not which field holds a value. A field
 * that is hidden keeps the value the user gave it, so reading EOSP whenever it
 * is set would make a report that stopped arriving keep the EOSP instant. EOSP
 * falls back to Report To only while the ship arrives, for a report saved
 * before EOSP had its own field.
 */
const getPeriodEnd = (formState: FormState) => {
  const reportTo = getReportDate(formState, 'reportTo');
  const arrivingAtEosp = getFieldValueFromFormState(formState, 'voyageItinerary', 'arrivingAtEosp') === true;
  if (!arrivingAtEosp) {
    return reportTo;
  }
  return getReportDate(formState, 'eosp') ?? reportTo;
};

// The Report Period in hours, unrounded. The control rounds it on screen.
const calculateReportPeriod = (reportFrom: Date | undefined, periodEnd: Date | undefined) => {
  if (!reportFrom || !periodEnd) return undefined;
  return differenceInMilliseconds(periodEnd, reportFrom) / (1000 * 60 * 60);
};

const getVoyageItineraryDate = (formState: FormState, fieldId: string) =>
  getDate(getFieldValueFromFormState(formState, 'voyageItinerary', fieldId));

const getVoyageItineraryBoolean = (formState: FormState, fieldId: string): boolean | undefined =>
  getFieldValueFromFormState(formState, 'voyageItinerary', fieldId) as boolean | undefined;

// Derives the calculated Time in ECA / In ECA fields from the current Enter ECA, Exit ECA,
// Previously in ECA and report period values. Time in ECA is unrounded. The
// control rounds it on screen.
const calculateEcaFields = (
  enterEca: Date | undefined,
  exitEca: Date | undefined,
  previouslyInEca: boolean,
  reportFrom: Date | undefined,
  periodEnd: Date | undefined,
  reportPeriod: number | undefined
): { timeInEca: number | undefined; inEca: boolean } => {
  const hasEcaTimestamp = !!enterEca || !!exitEca;
  if (!hasEcaTimestamp || !reportFrom || !periodEnd) {
    return previouslyInEca ? { timeInEca: reportPeriod, inEca: true } : { timeInEca: 0, inEca: false };
  }
  const exitDiff = exitEca ? differenceInMilliseconds(exitEca, reportFrom) : 0;
  const enterDiff = enterEca ? differenceInMilliseconds(periodEnd, enterEca) : 0;
  const durationInMs = previouslyInEca
    ? exitDiff + enterDiff
    : differenceInMilliseconds(exitEca ?? periodEnd, enterEca ?? reportFrom);

  const inEca = previouslyInEca ? !!enterEca : !exitEca;

  return { timeInEca: Math.max(durationInMs / (1000 * 60 * 60), 0), inEca };
};

export function buildOperationalVm(
  formState: FormState,
  vesselSpec: VesselSpec | undefined,
  vesselParticulars: VesselParticulars | undefined,
  portCallPurposes?: PortCallPurpose[]
): OperationalVm {
  const calculateTrim = () => {
    const draughtAft = getFieldValueFromFormState(formState, 'operatingConditions', 'draughtAft');
    const draughtForward = getFieldValueFromFormState(formState, 'operatingConditions', 'draughtForward');

    if (draughtAft == null || draughtForward == null) return undefined;

    const aft = parseFloat('' + draughtAft);
    const forward = parseFloat('' + draughtForward);
    if (isNaN(aft) || isNaN(forward)) return undefined;

    return aft - forward;
  };

  const enterEca = getVoyageItineraryDate(formState, 'enterEca');
  const exitEca = getVoyageItineraryDate(formState, 'exitEca');
  const previouslyInEcaRaw = getVoyageItineraryBoolean(formState, 'previouslyInEca');
  const previouslyInEca = previouslyInEcaRaw === true;
  const reportFrom = getReportDate(formState, 'reportFrom');
  const periodEnd = getPeriodEnd(formState);
  const reportPeriod = calculateReportPeriod(reportFrom, periodEnd);
  const awaitingOrders = getVoyageItineraryBoolean(formState, 'awaitingOrders') ?? false;

  const { timeInEca, inEca } = calculateEcaFields(
    enterEca,
    exitEca,
    previouslyInEca,
    reportFrom,
    periodEnd,
    reportPeriod
  );

  // Enter ECA just needs to fall within the report period, unless the vessel was previously in
  // ECA — in which case it's a re-entry, so it must also come after Exit ECA.
  const enterEcaError = (() => {
    if (!reportFrom || !periodEnd || !enterEca) return undefined;

    const isWithinPeriod = enterEca >= reportFrom && enterEca <= periodEnd;
    if (!previouslyInEca) return isWithinPeriod ? undefined : ENTER_ECA_ERROR;

    const isAfterExit = !!exitEca && enterEca >= exitEca;
    return isWithinPeriod && isAfterExit ? undefined : ENTER_ECA_PREVIOUSLY_IN_ECA_ERROR;
  })();

  // Exit ECA just needs to fall within the report period, unless the vessel wasn't previously in
  // ECA — in which case it must also come after Enter ECA.
  const exitEcaError = (() => {
    if (!reportFrom || !periodEnd || !exitEca) return undefined;

    const isWithinPeriod = exitEca >= reportFrom && exitEca <= periodEnd;
    if (previouslyInEca) return isWithinPeriod ? undefined : EXIT_ECA_ERROR;

    const isAfterEnter = enterEca ? exitEca >= enterEca : previouslyInEcaRaw === undefined;
    return isWithinPeriod && isAfterEnter ? undefined : EXIT_ECA_ERROR;
  })();

  // Enter ECA and Exit ECA each enabled once there was room for a fresh entry,
  // or the field already held a value to edit. The form-rule template states
  // both now, as isFieldDisabled on each field, so the markup asks the rule and
  // this builder answers nothing about it. The rules read the same inputs
  // through the report form state. See Disabled fields in the form-rule
  // language doc.

  const maxReportDurationHours = getMaxReportDurationHours(vesselSpec);

  const reportDurationError = (() => {
    const operationType = getFieldValueFromFormState(formState, 'operatingConditions', 'operationType') as
      | SelectOption
      | undefined;
    if (operationType?.code === DRY_DOCK_OR_REPAIR_OPERATION_TYPE) return;

    if (reportPeriod == null) return;

    return reportPeriod > maxReportDurationHours
      ? `Report period cannot exceed ${maxReportDurationHours} hours.`
      : undefined;
  })();

  const getShowBallastCodeWarning = () => {
    const ballastCode = getFieldFromFormState(formState, 'miscellaneous', 'ballastCode') as StateField<StateFieldType>;

    return ballastCode?.value == null && ballastCode?.pristine === false;
  };

  const charterPartySpeedLimit = vesselParticulars?.charterPartySpeed || FALLBACK_CHARTER_PARTY_SPEED;
  const cpSpeed = getFieldValueFromFormState(formState, 'vesselDetails', 'cpSpeed');
  const cpSpeedWarning =
    typeof cpSpeed === 'number' && cpSpeed > charterPartySpeedLimit
      ? `Charter Party speed should be between 0 and ${charterPartySpeedLimit} Knots.`
      : undefined;

  const constantWeight = getFieldFromFormState(
    formState,
    'stabilityConditions',
    'constantWeight'
  ) as StateField<StateFieldType>;
  const isConstantWeightBelowMin =
    typeof constantWeight?.value === 'number' && constantWeight.value < CONSTANT_WEIGHT_MIN;
  const constantWeightErrorTooltip = isConstantWeightBelowMin ? CONSTANT_WEIGHT_MIN_ERROR : undefined;

  const portCallReasonCategories = groupPortCallPurposes(portCallPurposes);

  const isVesselDetailsValid = isSectionValid(formState['vesselDetails']);
  const isMiscellaneousValid = isSectionValid(formState['miscellaneous']);
  const isReportTimeValid = isSectionValid(formState['reportTime']);
  const isPortAndPositionValid = isSectionValid(formState['portAndPosition']);
  const isStabilityConditionsValid = isSectionValid(formState['stabilityConditions']);
  const isPortOperationsTimestampValid = isSectionValid(formState['portOperationsTimestamp']);
  const isPoolSpecificFieldsValid = isSectionValid(formState['poolSpecificFields']);
  const isOperatingConditionsValid = isSectionValid(formState['operatingConditions']);
  const isNextPortValid = isSectionValid(formState['nextPort']);
  const isVoyageItineraryValid = isSectionValid(formState['voyageItinerary']);
  const isPortCallReasonsValid = isSectionValid(formState['portCallReasons']);

  const isOperationalValid =
    isVesselDetailsValid &&
    isMiscellaneousValid &&
    isReportTimeValid &&
    isPortAndPositionValid &&
    isStabilityConditionsValid &&
    isPortOperationsTimestampValid &&
    isPoolSpecificFieldsValid &&
    isOperatingConditionsValid &&
    isNextPortValid &&
    isVoyageItineraryValid &&
    isPortCallReasonsValid;

  return {
    isVesselDetailsValid,
    isMiscellaneousValid,
    isReportTimeValid,
    isPortAndPositionValid,
    isStabilityConditionsValid,
    isPortOperationsTimestampValid,
    isPoolSpecificFieldsValid,
    isOperatingConditionsValid,
    isNextPortValid,
    isVoyageItineraryValid,
    isOperationalValid,
    reportPeriod,
    reportTo: getReportDate(formState, 'reportTo'),
    reportFrom,
    trim: calculateTrim(),
    operatingCodeOptions: getOperatingCodeOptions(vesselSpec),
    showBallastCodeWarning: getShowBallastCodeWarning(),
    cpSpeedWarning,
    timeInEca,
    inEca,
    enterEcaError,
    exitEcaError,
    awaitingOrders,
    previouslyInEca,
    isConstantWeightBelowMin,
    constantWeightErrorTooltip,
    maxReportDurationHours,
    reportDurationError,
    portCallReasonCategories,
    isPortCallReasonsValid,
  };
}
